/**
 * ConceptDetail — Phase 5 View 3 (concept page).
 *
 * The Phase 3 lifecycle API has no per-concept GET endpoint, so we derive
 * detail from the version-level `inspect.concepts` index. Tabs that need
 * data outside that index (creative evidence, version history, cognitive
 * relations) show empty states until those endpoints are wired up.
 *
 * URL: /admin/ontology/:clientUid/concepts/:conceptId?version=&tab=
 *
 * NOTE: `:conceptId` is the concept *name* (URL-encoded), since the index
 * uses name as its stable identifier.
 */
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  BookOpen,
  Activity,
  Brain,
  Anchor,
  Image as ImageIcon,
  History as HistoryIcon,
  Info,
} from 'lucide-react';
import { clsx } from 'clsx';
import { pdoLifecycleApi } from '@/services/api';
import { MaturityBadge } from '@/components/pdo/StatusBadges';
import { EmptyState } from '@/components/pdo/EmptyState';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import { useFetch, categoryColor } from '@/components/pdo/utils';

type Tab =
  | 'overview'
  | 'causal'
  | 'cognitive'
  | 'grounding'
  | 'creative'
  | 'history';

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: BookOpen },
  { id: 'causal', label: 'Causal Evidence', icon: Activity },
  { id: 'cognitive', label: 'Cognitive Relations', icon: Brain },
  { id: 'grounding', label: 'Grounding', icon: Anchor },
  { id: 'creative', label: 'Creative Evidence', icon: ImageIcon },
  { id: 'history', label: 'Version History', icon: HistoryIcon },
];

type ConceptRecord = Record<string, unknown> & { name?: string };

function getStr(c: ConceptRecord, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = c[k];
    if (typeof v === 'string' && v) return v;
  }
  return undefined;
}
function getNum(c: ConceptRecord, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = c[k];
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
  }
  return undefined;
}

export function ConceptDetail() {
  const { clientUid, conceptId } = useParams<{
    clientUid: string;
    conceptId: string;
  }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const version = searchParams.get('version');
  const tab = (searchParams.get('tab') as Tab) ?? 'overview';

  const inspect = useFetch(
    () =>
      clientUid && version
        ? pdoLifecycleApi.inspectVersion(clientUid, version)
        : Promise.resolve(null as never),
    [clientUid, version],
  );

  if (!clientUid || !conceptId) {
    return <EmptyState title="Missing concept identifier" />;
  }

  const concept = inspect.data?.concepts.find(
    (c) => getStr(c, 'name') === conceptId,
  ) as ConceptRecord | undefined;

  const category = concept ? getStr(concept, 'category', 'concept_category') : undefined;
  const entityType = concept ? getStr(concept, 'entity_type', 'type') : undefined;
  const maturity = concept ? getStr(concept, 'maturity_stage') : undefined;
  const definition = concept ? getStr(concept, 'definition', 'description') : undefined;
  const cognitiveMechanism = concept
    ? getStr(concept, 'cognitive_mechanism')
    : undefined;
  const causalStrength = concept ? getNum(concept, 'causal_strength') : undefined;
  const stability = concept ? getNum(concept, 'stability', 'avg_stability') : undefined;
  const confidence = concept ? getNum(concept, 'confidence') : undefined;

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-start justify-between gap-4 px-1 pb-4 border-b border-slate-200 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy-900"
          >
            <ArrowLeft size={14} />
            Back
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-navy-900 truncate flex items-center gap-2">
              {category && (
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: categoryColor(category) }}
                />
              )}
              {decodeURIComponent(conceptId)}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">
              {clientUid} · {version ?? '—'}
            </p>
          </div>
        </div>
        <Link
          to={`/admin/ontology/${clientUid}/concepts?version=${version ?? ''}`}
          className="text-xs font-medium text-teal-700 hover:underline"
        >
          ← Back to inspector
        </Link>
      </header>

      {inspect.error && (
        <ErrorBanner error={inspect.error} onRetry={inspect.refetch} />
      )}

      <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() =>
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('tab', t.id);
                    return next;
                  },
                  { replace: true },
                )
              }
              className={clsx(
                'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px',
                active
                  ? 'border-teal-500 text-teal-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700',
              )}
            >
              <Icon size={13} />
              {t.label}
            </button>
          );
        })}
      </div>

      {inspect.loading && !inspect.data ? (
        <div className="flex items-center justify-center py-16 text-xs text-slate-400">
          <Loader2 size={14} className="animate-spin mr-2" />
          Loading concept…
        </div>
      ) : !concept ? (
        <EmptyState
          title="Concept not found in this version's index"
          hint={`No concept named "${decodeURIComponent(conceptId)}" exists in the RESOLUTION-stage index for ${version ?? 'this version'}.`}
          action={
            <Link
              to={`/admin/ontology/${clientUid}/concepts?version=${version ?? ''}`}
              className="text-xs font-medium text-teal-700 hover:underline"
            >
              Back to catalog
            </Link>
          }
        />
      ) : (
        <>
          {tab === 'overview' && (
            <OverviewTab
              concept={concept}
              category={category}
              entityType={entityType}
              maturity={maturity}
              definition={definition}
              cognitiveMechanism={cognitiveMechanism}
              stability={stability}
              confidence={confidence}
              causalStrength={causalStrength}
            />
          )}
          {tab === 'causal' && (
            <CausalTab
              causalStrength={causalStrength}
              stability={stability}
              confidence={confidence}
            />
          )}
          {tab === 'cognitive' && <CognitiveTab />}
          {tab === 'grounding' && <GroundingTab concept={concept} />}
          {tab === 'creative' && <CreativeTab />}
          {tab === 'history' && <HistoryTab />}
        </>
      )}
    </div>
  );
}

function OverviewTab(props: {
  concept: ConceptRecord;
  category?: string;
  entityType?: string;
  maturity?: string;
  definition?: string;
  cognitiveMechanism?: string;
  stability?: number;
  confidence?: number;
  causalStrength?: number;
}) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <section className="col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-4">
        <div className="flex items-center gap-2 text-xs">
          {props.entityType && (
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">
              {props.entityType}
            </span>
          )}
          {props.category && (
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
              {props.category}
            </span>
          )}
          {props.maturity && <MaturityBadge stage={props.maturity} />}
        </div>
        {props.definition ? (
          <div>
            <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              Definition
            </h3>
            <p className="text-sm text-slate-800 leading-relaxed">
              {props.definition}
            </p>
          </div>
        ) : (
          <EmptyState compact title="No definition recorded" />
        )}
        {props.cognitiveMechanism && (
          <div>
            <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              Cognitive mechanism
            </h3>
            <p className="text-sm text-slate-700 leading-relaxed italic">
              {props.cognitiveMechanism}
            </p>
          </div>
        )}
      </section>
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3">
        <div>
          <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
            Headline metrics
          </h3>
          <Metric label="causal_strength" value={props.causalStrength} />
          <Metric label="stability" value={props.stability} />
          <Metric label="confidence" value={props.confidence} />
        </div>
        <RawJson concept={props.concept} />
      </section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="flex items-center justify-between text-xs py-0.5">
      <span className="font-mono text-slate-500">{label}</span>
      <span className="font-mono text-navy-900">
        {value === undefined ? '–' : value.toFixed(3)}
      </span>
    </div>
  );
}

function RawJson({ concept }: { concept: ConceptRecord }) {
  return (
    <details>
      <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-700">
        Raw record
      </summary>
      <pre className="mt-1 max-h-56 overflow-auto bg-slate-900 text-slate-100 rounded p-2 text-[10px] leading-relaxed">
        {JSON.stringify(concept, null, 2)}
      </pre>
    </details>
  );
}

function CausalTab({
  causalStrength,
  stability,
  confidence,
}: {
  causalStrength?: number;
  stability?: number;
  confidence?: number;
}) {
  return (
    <div className="space-y-4">
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="grid grid-cols-3 gap-4">
          <Headline label="Causal" value={causalStrength} />
          <Headline label="Stability" value={stability} />
          <Headline label="Confidence" value={confidence} />
        </div>
      </section>
      <ChartUnavailable />
    </div>
  );
}

function Headline({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
        {label}
      </p>
      <p className="text-2xl font-mono font-semibold text-navy-900">
        {value === undefined ? '–' : value.toFixed(3)}
      </p>
    </div>
  );
}

function ChartUnavailable() {
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 flex items-start gap-2">
      <Info size={13} className="flex-shrink-0 mt-0.5" />
      <p>
        Per-round stability history isn't yet exposed by the lifecycle{' '}
        <code>inspect</code> endpoint — the index records only the latest
        causal/stability/confidence values per version.
      </p>
    </div>
  );
}

function CognitiveTab() {
  return (
    <EmptyState
      title="No cognitive relations yet"
      hint="Cognitive relations will appear after Phase 2 R1 integration. The lifecycle inspect payload doesn't carry this data yet."
      icon={<Brain size={18} />}
    />
  );
}

function GroundingTab({ concept }: { concept: ConceptRecord }) {
  // The concepts_index sometimes carries grounding hints — we surface
  // anything that looks like list-shaped fields under common names.
  const products = (concept['products'] ?? concept['product_grounding']) as
    | Array<Record<string, unknown>>
    | undefined;
  const scenarios = (concept['scenario_activations'] ?? concept['scenarios']) as
    | Array<Record<string, unknown>>
    | undefined;
  const fmqs = (concept['fmqs'] ?? concept['foundation_model_queries']) as
    | Array<Record<string, unknown>>
    | undefined;

  if (!products && !scenarios && !fmqs) {
    return (
      <EmptyState
        title="Grounding data not in concept index"
        hint="The RESOLUTION-stage index doesn't carry product / scenario / FMQ links for this version."
        icon={<Anchor size={18} />}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PassthroughList title="Products" rows={products} />
      <PassthroughList title="Scenarios" rows={scenarios} />
      <PassthroughList title="Foundation model queries" rows={fmqs} />
    </div>
  );
}

function PassthroughList({
  title,
  rows,
}: {
  title: string;
  rows: Array<Record<string, unknown>> | undefined;
}) {
  if (!rows || rows.length === 0) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <header className="px-4 py-2 border-b border-slate-200 bg-slate-50">
        <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
          {title} ({rows.length})
        </h3>
      </header>
      <ul className="divide-y divide-slate-100 text-xs">
        {rows.slice(0, 25).map((r, i) => (
          <li key={i} className="px-4 py-1.5 font-mono text-slate-700">
            {JSON.stringify(r)}
          </li>
        ))}
        {rows.length > 25 && (
          <li className="px-4 py-1.5 text-slate-400 italic">
            +{rows.length - 25} more
          </li>
        )}
      </ul>
    </section>
  );
}

function CreativeTab() {
  return (
    <EmptyState
      title="Creative evidence pending"
      hint="A per-concept creatives endpoint isn't wired yet. The Phase 4 extraction status surfaces creative-level data — see the Build Inspector's extraction dashboard."
      icon={<ImageIcon size={18} />}
    />
  );
}

function HistoryTab() {
  return (
    <EmptyState
      title="Version history pending"
      hint="A per-concept history endpoint isn't wired yet. Compare two versions side-by-side from the version browser to see name-level changes."
      icon={<HistoryIcon size={18} />}
    />
  );
}
